import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Calendar, Shield, TrendingUp, Plus } from 'lucide-react';
import EnhancedLightCard from '../../components/UI/EnhancedLightCard';
import CustomScrollbar from '../../components/UI/CustomScrollbar';
import RejectionReasonModal from '../../components/UI/RejectionReasonModal';
import { useAdminDashboard, useRecentActivity, usePendingApprovals } from '../../hooks/useDashboardData';
import { useAppSelector } from '../../store/hooks';
import { eventAPI } from '../../services/eventAPI';
import { PendingApproval } from '../../services/dashboardService';
import { webSocketService } from '../../services/websocketService';
import { authAPI } from '../../services/api';
import { setUser } from '../../store/slices/authSlice';
import { store } from '../../store/store';
import { logError } from '../../utils/errorLogger';

const AdminDashboard: React.FC = () => {
  const { theme } = useAppSelector((state) => state.ui);
  const { data: statsData, loading: statsLoading, error: statsError } = useAdminDashboard();
  const { data: activityData, loading: activityLoading } = useRecentActivity();
  const { data: approvalsData, loading: approvalsLoading, error: approvalsError, refetch: refetchApprovals } = usePendingApprovals();
  
  // State for approval actions
  const [processingApproval, setProcessingApproval] = useState<string | null>(null);
  const [approvalMessage, setApprovalMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [rejectionModalOpen, setRejectionModalOpen] = useState(false);
  const [pendingRejection, setPendingRejection] = useState<PendingApproval | null>(null);
  
  // Force refresh function that clears cache and refetches
  const forceRefreshApprovals = async () => {
    // Clear dashboard cache to ensure fresh data
    const { apiClient } = await import('../../services/apiClient');
    apiClient.invalidateCache('/dashboard/');
    // Refetch the data
    await refetchApprovals();
  };

  // Refresh user profile on dashboard load to get latest roles
  useEffect(() => {
    const refreshUserProfile = async () => {
      try {
        const profileResponse = await authAPI.getProfile();
        if (profileResponse.data.success && profileResponse.data.data) {
          const updatedUser = {
            ...profileResponse.data.data,
            roles: profileResponse.data.data.roles || [profileResponse.data.data.role],
            active_role: profileResponse.data.data.active_role || profileResponse.data.data.role,
          };
          store.dispatch(setUser(updatedUser));
        }
      } catch (err) {
        logError('Failed to refresh user profile', err);
      }
    };
    
    refreshUserProfile();
  }, []);

  // Real-time WebSocket updates
  useEffect(() => {
    const handleGuestResponse = (data: any) => {
      // Refresh dashboard data when any guest responds
      forceRefreshApprovals();
    };

    const handleStatisticsUpdate = (data: any) => {
      forceRefreshApprovals();
    };

    // Subscribe to real-time updates
    const unsubscribeGuestResponse = webSocketService.on('guest_response', handleGuestResponse);
    const unsubscribeStatistics = webSocketService.on('event_statistics_update', handleStatisticsUpdate);

    return () => {
      unsubscribeGuestResponse();
      unsubscribeStatistics();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Remove dependencies to prevent re-subscription

  // Show loading state
  if (statsLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#33FF00] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading dashboard data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (statsError) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="text-red-600">
              <Shield className="h-6 w-6" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error Loading Dashboard</h3>
              <p className="mt-1 text-sm text-red-700">{statsError}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Prepare stats data
  const stats = statsData ? [
    {
      name: 'Total Users',
      value: statsData.users.total.toLocaleString(),
      icon: <Users />,
      trend: { value: Math.abs(statsData.users.growth_rate), positive: statsData.users.growth_rate >= 0 },
    },
    {
      name: 'Active Events',
      value: statsData.events.active.toString(),
      icon: <Calendar />,
      trend: { value: Math.abs(statsData.events.growth_rate), positive: statsData.events.growth_rate >= 0 },
    },
    {
      name: 'Total Hosts',
      value: statsData.users.hosts.toString(),
      icon: <Shield />,
      trend: { value: Math.abs(statsData.users.growth_rate), positive: statsData.users.growth_rate >= 0 },
    },
    {
      name: 'Response Rate',
      value: `${statsData.guests.response_rate.toFixed(1)}%`,
      icon: <TrendingUp />,
      trend: { value: Math.abs(statsData.guests.response_rate), positive: statsData.guests.response_rate >= 50 },
    },
  ] : [];

  const recentActivity = activityData?.activities || [];

  // Handle event approval
  const handleApprove = async (approval: PendingApproval) => {
    setProcessingApproval(approval.id);
    setApprovalMessage(null);
    
    try {
      await eventAPI.approveEvent(approval.id, { reason: 'Event approved by admin' });
      setApprovalMessage({ type: 'success', text: `Event "${approval.title}" has been approved!` });
      
      // Force refresh the approvals list by clearing cache first
      await forceRefreshApprovals();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to approve event';
      setApprovalMessage({ type: 'error', text: errorMessage });
    } finally {
      setProcessingApproval(null);
    }
  };

  // Handle event rejection - open modal
  const handleReject = (approval: PendingApproval) => {
    setPendingRejection(approval);
    setRejectionModalOpen(true);
  };

  // Submit rejection with reason
  const handleRejectSubmit = async (reason: string) => {
    if (!pendingRejection) return;

    setRejectionModalOpen(false);
    setProcessingApproval(pendingRejection.id);
    setApprovalMessage(null);
    
    try {
      await eventAPI.rejectEvent(pendingRejection.id, { reason });
      setApprovalMessage({ type: 'success', text: `Event "${pendingRejection.title}" has been rejected.` });
      
      // Force refresh the approvals list by clearing cache first
      await forceRefreshApprovals();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to reject event';
      setApprovalMessage({ type: 'error', text: errorMessage });
    } finally {
      setProcessingApproval(null);
      setPendingRejection(null);
    }
  };


  return (
    <div className="space-y-4 pb-4 sm:pb-6">
      {/* Success/Error Message */}
      {approvalMessage && (
        <div className={`max-w-6xl mx-4 sm:mx-auto rounded-lg p-3 sm:p-4 ${
          approvalMessage.type === 'success' 
            ? theme === 'dark' 
              ? 'bg-green-900/30 border border-green-700/50 text-green-300' 
              : 'bg-green-50 border border-green-200 text-green-800'
            : theme === 'dark'
              ? 'bg-red-900/30 border border-red-700/50 text-red-300'
              : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          <div className="flex items-center justify-between">
            <span className="text-xs sm:text-sm font-medium pr-2">{approvalMessage.text}</span>
            <button 
              onClick={() => setApprovalMessage(null)}
              className={`flex-shrink-0 text-lg leading-none ${
                approvalMessage.type === 'success' 
                  ? theme === 'dark' ? 'text-green-300 hover:text-green-200' : 'text-green-800 hover:text-green-600'
                  : theme === 'dark' ? 'text-red-300 hover:text-red-200' : 'text-red-800 hover:text-red-600'
              }`}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 mt-4">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <div>
            <h1 className={`text-xl sm:text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Admin Dashboard</h1>
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-3 items-center">
            <Link
              to="/admin/events/create"
              className={`inline-flex items-center px-3 sm:px-6 py-2 sm:py-3 rounded-xl text-xs sm:text-base font-semibold transition-all duration-300 hover:scale-105 ${
                theme === 'dark'
                  ? 'bg-gradient-to-r from-[#33FF00] to-[#2BD400] text-black hover:from-[#2BD400] hover:to-[#33FF00] shadow-lg hover:shadow-[#33FF00]/25'
                  : 'bg-gradient-to-r from-[#33FF00] to-[#2BD400] text-black hover:from-[#2BD400] hover:to-[#33FF00] shadow-lg hover:shadow-[#33FF00]/25'
              }`}
            >
              <Plus className="h-4 w-4 sm:h-5 sm:w-5 sm:mr-3" />
              <span className="hidden sm:inline">Create Event</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Stats — compact horizontal cards for clear hierarchy */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 max-w-6xl mx-auto px-4 sm:px-6">
        {stats.map((item) => (
          <EnhancedLightCard
            key={item.name}
            title={item.name}
            value={item.value}
            icon={item.icon}
            trend={item.trend}
            compactHorizontal
          />
        ))}
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 max-w-6xl mx-auto px-4 sm:px-6">
        {/* Recent Activity */}
        <div className={`relative overflow-hidden rounded-2xl shadow-2xl border backdrop-blur-sm ${
          theme === 'dark' 
            ? 'bg-black/80 border-gray-800/50' 
            : 'bg-white/90 border-gray-200/50'
        }`}>
          {/* Premium background with subtle patterns */}
          <div className={`absolute inset-0 ${
            theme === 'dark' 
              ? 'bg-gradient-to-br from-black via-gray-900/90 to-black' 
              : 'bg-gradient-to-br from-white via-gray-50/80 to-white'
          }`}></div>
          
          {/* Subtle texture overlay */}
          <div className={`absolute inset-0 opacity-5 ${
            theme === 'dark' 
              ? 'bg-gradient-to-br from-[#33FF00]/10 via-transparent to-[#33FF00]/5' 
              : 'bg-gradient-to-br from-[#33FF00]/5 via-transparent to-[#33FF00]/3'
          }`}></div>
          
          
          {/* Content */}
          <div className="relative z-10">
            <div className={`px-4 sm:px-6 py-3 sm:py-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-base sm:text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-black'}`}>Recent Activity</h3>
            </div>
            <div className="px-4 sm:px-6 py-3 sm:py-4">
              {activityLoading ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#33FF00]"></div>
                </div>
              ) : recentActivity.length > 0 ? (
                <div className="relative">
                  <CustomScrollbar maxHeight="max-h-80">
                    <div className="space-y-4">
                      {recentActivity.map((activity) => (
                        <div key={activity.id} className="flex items-start space-x-2 sm:space-x-3">
                          <div className="flex-shrink-0">
                            <div className="h-2 w-2 bg-[#33FF00] rounded-full mt-2"></div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs sm:text-sm ${theme === 'dark' ? 'text-white' : 'text-black'}`}>{activity.message}</p>
                            <p className={`text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                              {new Date(activity.time).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CustomScrollbar>
                  {recentActivity.length > 4 && (
                    <div className={`absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t ${
                      theme === 'dark' ? 'from-gray-900' : 'from-gray-50'
                    } to-transparent pointer-events-none`}></div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className={theme === 'dark' ? 'text-white' : 'text-black'}>No recent activity</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Pending Approvals */}
        <div className={`relative overflow-hidden rounded-2xl shadow-2xl border backdrop-blur-sm ${
          theme === 'dark' 
            ? 'bg-black/80 border-gray-800/50' 
            : 'bg-white/90 border-gray-200/50'
        }`}>
          {/* Premium background with subtle patterns */}
          <div className={`absolute inset-0 ${
            theme === 'dark' 
              ? 'bg-gradient-to-br from-black via-gray-900/90 to-black' 
              : 'bg-gradient-to-br from-white via-gray-50/80 to-white'
          }`}></div>
          
          {/* Subtle texture overlay */}
          <div className={`absolute inset-0 opacity-5 ${
            theme === 'dark' 
              ? 'bg-gradient-to-br from-[#33FF00]/10 via-transparent to-[#33FF00]/5' 
              : 'bg-gradient-to-br from-[#33FF00]/5 via-transparent to-[#33FF00]/3'
          }`}></div>
          
          
          {/* Content */}
          <div className="relative z-10">
            <div className={`px-4 sm:px-6 py-3 sm:py-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-base sm:text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-black'}`}>Pending Approvals</h3>
            </div>
            <div className="px-4 sm:px-6 py-3 sm:py-4">
              {approvalsLoading ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#33FF00]"></div>
                </div>
              ) : approvalsError ? (
                <div className="text-center py-8">
                  <p className="text-red-500">Error loading approvals</p>
                </div>
              ) : approvalsData && approvalsData.approvals.length > 0 ? (
                <CustomScrollbar maxHeight="max-h-80">
                  <div className="space-y-3">
                    {approvalsData.approvals.map((approval) => (
                      <div key={approval.id} className={`flex items-start space-x-2 sm:space-x-3 p-2 sm:p-3 border rounded-lg transition-colors ${
                        theme === 'dark' 
                          ? 'border-gray-700 hover:bg-gray-800' 
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}>
                        <div className="flex-shrink-0">
                          <div className="h-2 w-2 bg-[#33FF00] rounded-full mt-2"></div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-0">
                            <h4 className={`text-xs sm:text-sm font-medium truncate ${theme === 'dark' ? 'text-white' : 'text-black'}`}>{approval.title}</h4>
                            <span className={`text-xs px-2 py-1 rounded-full self-start sm:self-auto ${
                              theme === 'dark' 
                                ? 'text-black bg-yellow-400' 
                                : 'text-black bg-yellow-100'
                            }`}>
                              Pending
                            </span>
                          </div>
                          <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                            Host: {approval.host_name} • {approval.guest_count} guests
                          </p>
                          {approval.event_date && (
                            <p className={`text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                              Date: {new Date(approval.event_date).toLocaleDateString()}
                            </p>
                          )}
                          <p className={`text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} truncate`}>
                            Location: {approval.location}
                          </p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            <button 
                              onClick={() => handleApprove(approval)}
                              disabled={processingApproval === approval.id}
                              className={`text-xs px-2 sm:px-3 py-1 rounded transition-colors flex-1 sm:flex-none min-w-[80px] ${
                                processingApproval === approval.id
                                  ? theme === 'dark' 
                                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                  : 'bg-[#33FF00] text-black hover:bg-[#2BD400]'
                              }`}
                            >
                              {processingApproval === approval.id ? 'Processing...' : 'Approve'}
                            </button>
                            <button 
                              onClick={() => handleReject(approval)}
                              disabled={processingApproval === approval.id}
                              className={`text-xs px-2 sm:px-3 py-1 rounded transition-colors flex-1 sm:flex-none min-w-[80px] ${
                                processingApproval === approval.id
                                  ? theme === 'dark' 
                                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                  : 'bg-red-500 text-white hover:bg-red-600'
                              }`}
                            >
                              {processingApproval === approval.id ? 'Processing...' : 'Reject'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CustomScrollbar>
              ) : (
                <div className="text-center py-8">
                  <p className={theme === 'dark' ? 'text-white' : 'text-black'}>No pending approvals</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Rejection Reason Modal */}
      <RejectionReasonModal
        isOpen={rejectionModalOpen}
        onClose={() => {
          setRejectionModalOpen(false);
          setPendingRejection(null);
        }}
        onSubmit={handleRejectSubmit}
        eventTitle={pendingRejection?.title}
      />

    </div>
  );
};

export default AdminDashboard;
